import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useRef, useEffect } from "react";

import { QueryKeys, queryClient } from "@/api/core";
import { type Metrics } from "@/schemas";
import { addSource } from "@/utils/ads";

const useMetrics = (
  metrics: Metrics | null | undefined,
  source?: string,
  /** Delay the impression until the ad has actually been seen. */
  isEnabled = true,
): void => {
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);

  // Fetch the impression URL (if any) upon displaying the ad.
  // Don't fetch it more than once.
  useQuery({
    queryKey: QueryKeys.ads.impression(metrics?.impression!),
    queryFn: () => {
      if (metrics?.impression) {
        return axios.get(
          source ? addSource(metrics.impression, source) : metrics.impression,
        );
      }
    },
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: Boolean(metrics?.impression) && isEnabled,
  });

  /** Invalidate query cache for ads. */
  const bustCache = (): void => {
    queryClient.invalidateQueries({ queryKey: QueryKeys.ads.global });
  };

  // Wait until the ad expires, then invalidate cache.
  useEffect(() => {
    if (metrics?.expires_at) {
      const delta =
        new Date(metrics.expires_at).getTime() - new Date().getTime();
      timer.current = setTimeout(bustCache, delta);
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [metrics?.expires_at]);
};

export { useMetrics };
